// Normalize the free text pilots type into travel rows so the Travel board
// groups people who are actually in the same place together.
//
// The office sheet is consistent ("JNU", "KK"). Pilots adding their own travel
// by hand are not: Pat typed "Juneau" and "kk" on 2026-08-29 and ended up in a
// group of one, sitting apart from the four other pilots leaving JNU that
// morning. That is how someone misses a shared charter seat.
//
// TWO SEPARATE MAPS ON PURPOSE. "kk" is Kake as a PLACE and the Kake lodge as a
// HOTEL. One combined map would collapse those into each other.
//
// THIS IS A NORMALIZER, NOT AN ALLOWLIST. Anything not in a map passes straight
// through (trimmed, whitespace-collapsed, uppercased). A value we have never
// seen must still reach the pilot's screen unchanged — never dropped, never
// blanked. Case-only variants ("yak" vs "YAK", "aspen" vs "Aspen") are handled
// by that pass-through, so the maps only carry genuinely different spellings.

// comparison key: case and punctuation are noise, so "inn@creek st",
// "Inn @ Creek St" and "INNCREEKST" all reduce to the same thing
const canon = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Codes on the right match what the office sheet and the slip location table
// already use, so a normalized pilot entry lines up with an office row.
const LOCATIONS = {
  yak: "YAK", yakutat: "YAK",
  ktn: "KTN", ketch: "KTN", ketchikan: "KTN",
  jnu: "JNU", jun: "JNU", juneau: "JNU",
  kake: "KAKE", kk: "KAKE", ovk: "KAKE",
  klw: "KLW", klawock: "KLW",
  sit: "SIT", sitka: "SIT",
  // absorbed from travelCheck.js, which kept its own copy of this map until
  // 2026-08-29. One map, so the two can never drift apart again.
  elfincove: "ELF", skagway: "SKG", wrangell: "WRG",
  icystraitpoint: "ISP", hoonah: "ISP", haines: "HNS",
};

const HOTELS = {
  // The winning spelling is whatever the office already writes most, so pilot
  // shorthand folds INTO the familiar word and no existing row changes name.
  // Office counts on 2026-08-29: KK 224, INN AT CREEK 80, BHI 77.
  // BHI is the Blue Heron Inn in YAKUTAT (37 rows fly JNU to YAK; its checkout
  // rows all read "OC PS OFF"). "GR. BLUE HERON" is a KLAWOCK hotel and is
  // deliberately NOT folded in here — same-sounding name, 400 miles apart.
  bhi: "BHI", blueheron: "BHI", blueheroninn: "BHI",
  // KLW FIREWEED and FIREWEED are both Klawock, so these do fold together.
  klwfireweed: "FIREWEED",
  // "Inn" on its own means the Inn at Creek Street here (Jill, 2026-08-29).
  // Whole-string match only, so "Cape Fox Inn" is untouched.
  inn: "INN AT CREEK", iacs: "INN AT CREEK", innatcreek: "INN AT CREEK",
  inncreekst: "INN AT CREEK", innatcreekst: "INN AT CREEK",
  innatcreekstreet: "INN AT CREEK",
  // The office books two different Aspens (ASPEN SITKA, ASPEN JUNEAU), so a
  // bare "aspen" is NOT folded into either — we cannot know which city, and
  // guessing would put a pilot at the wrong hotel.
  aspen: "ASPEN",
  kk: "KK", lodge: "KK",
};

// Fallback for anything not in a map: tidy it, don't reinterpret it.
const passthrough = (s) => String(s || "").trim().replace(/\s+/g, " ").toUpperCase();

function lookup(map, value) {
  if (value === null || value === undefined) return value;
  const raw = String(value).trim();
  if (!raw) return raw;
  return map[canon(raw)] || passthrough(raw);
}

// "Juneau" -> "JNU", "kake" -> "KAKE", "FREDERICK SND PS OFF" -> unchanged
export function normalizeLoc(value) {
  return lookup(LOCATIONS, value);
}

// Hotels whose name alone does not say which town. The office books an Aspen
// in both Sitka and Juneau, so a bare "aspen" is resolved from where the pilot
// is actually headed (Jill, 2026-08-29). If we cannot tell, it is left alone
// rather than guessed into the wrong town.
const AMBIGUOUS_HOTELS = {
  aspen: { SIT: "ASPEN SITKA", JNU: "ASPEN JUNEAU" },
};

// "kk" -> "KK", "blue heron inn" -> "BHI", "Cape Fox" -> "CAPE FOX".
// Pass the whole travel row as the second argument where you have it, so an
// ambiguous name can be resolved from the pilot's destination.
export function normalizeHotel(value, row) {
  const byTown = AMBIGUOUS_HOTELS[canon(value)];
  if (byTown) {
    // where they end up is where they sleep; fall back to where they left from
    const where = normalizeLoc(row?.to_loc) || normalizeLoc(row?.from_loc);
    if (where && byTown[where]) return byTown[where];
  }
  return lookup(HOTELS, value);
}

// Apply to a whole travel row on save, so the stored data is clean going
// forward. Only the three free-text place fields are touched; flight numbers,
// confirmations and notes are left exactly as typed.
export function normalizeTravelRow(row) {
  const out = { ...row };
  for (const k of ["from_loc", "to_loc", "cont_to"]) {
    if (k in out) out[k] = normalizeLoc(out[k]);
  }
  if ("hotel" in out) out.hotel = normalizeHotel(out.hotel, out);
  return out;
}
